import React, { useContext, useState } from 'react';
import { Pressable, Text } from 'react-native';
import { useQuery } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';

import NewExpense from 'app/src/components/Accounting/Expenses/new';
import EditExpense from 'app/src/components/Accounting/Expenses/edit';
import AuthContext from 'app/src/contexts/AuthContext';
import {
  ContentContainer,
  LinkContainer,
  LinkSpace,
  MainContainer,
  TableContainer,
  PageTitle,
  SubNavContainer,
} from 'app/src/elements';
import Modal from 'app/src/elements/Modal';
import { currency } from 'app/src/utils/stringHelpers';
import { dateShort } from 'app/src/utils/timeFormatter';

import { ASSET_EXPENSES } from './queries';

const baseColumns = [
  {
    key: 'id',
    label: 'Date',
    sort: 'desc',
    value: ({ entry }) => entry.txn.bankDate,
    content: dateShort,
  },
  {
    key: 'createdAt',
    label: 'Date Entered',
    value: ({ entry }) => entry.txn.createdAt,
    content: dateShort,
  },
  {
    key: 'method',
    label: 'Payment Method',
    value: ({ entry }) => entry.txn.method,
  },
  {
    key: 'reference',
    label: 'Invoice Number',
    value: ({ entry }) => entry.txn.reference,
  },
  {
    key: 'name',
    label: 'Vendor',
    value: ({ entry }) => entry.txn.name,
  },
  {
    key: 'account',
    label: 'Expense Account',
    value: ({ value }) => value.name,
  },
  {
    key: 'fee',
    label: 'Fee Charged',
    value: ({ value }) => value?.type,
  },
  {
    key: 'debitLessCredit',
    label: 'Line Amount',
    content: ({ value }) => currency(value),
  },
  {
    key: 'amount',
    label: 'Transaction Total',
    content: ({ entry }) => currency(entry.txn.amount),
  },
];

const AssetExpenses = ({ asset }) => {
  const { hasPolicy } = useContext(AuthContext);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const { data, refetch } = useQuery(ASSET_EXPENSES, {
    fetchPolicy: 'no-cache',
    variables: { id: asset.id },
  });

  const columns = [
    ...baseColumns,
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      hidden: !hasPolicy({ type: 'Txn', action: 'update' }),
      content: ({ entry }) => (
        <LinkContainer>
          <Pressable onPress={() => setShowEditModal(entry.txn)}>
            <Text>Edit</Text>
          </Pressable>
        </LinkContainer>
      )
    },
  ];

  return (
    <MainContainer>
      <ContentContainer>
        <PageTitle>Expenses</PageTitle>
        <br /><br />
        <TableContainer>
          {hasPolicy({ type: 'Txn', action: 'create' }) && (
            <SubNavContainer>
              <Pressable onPress={() => setShowAddModal('Expense')} >
                <Text>+ Add Expense</Text>
              </Pressable>
              <LinkSpace />

              <Pressable onPress={() => setShowAddModal('Bill')} >
                <Text>+ Add Bill</Text>
              </Pressable>
              <LinkSpace />

              <Pressable onPress={() => setShowAddModal('Credit Card Charge')} >
                <Text>+ Add Credit Card Charge</Text>
              </Pressable>
              <LinkSpace />

              <Pressable onPress={() => setShowAddModal('Internal Expense')} >
                <Text>+ Add Internal Expense</Text>
              </Pressable>
              <LinkSpace />
            </SubNavContainer>
          )}

          <DataTable.Simple
            data={data?.asset.txnLines || []}
            columns={columns}
            pageLength="All"
            htmlTable
          />
        </TableContainer>

        {showAddModal &&
          <Modal
            showModal
            setShowModal={setShowAddModal}
            modalType={`Add ${showAddModal}`}
          >
            <NewExpense
              setShowModal={setShowAddModal}
              type={showAddModal}
              refetch={refetch}
              asset={asset}
            />
          </Modal>
        }

        {showEditModal &&
          <Modal
            showModal
            setShowModal={setShowEditModal}
            modalType="Edit Expense"
          >
            <EditExpense
              txn={showEditModal}
              setShowModal={setShowEditModal}
              refetch={refetch}
            />
          </Modal>
        }
      </ContentContainer>
    </MainContainer>
  );
};

export default AssetExpenses;
